import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBreadcrumb from "@/components/base/PageBreadcrumb";
import RatingStars from "@/components/base/RatingStars";
import SectionHeading from "@/components/base/SectionHeading";
import PosterCard from "@/components/base/PosterCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatHeat, getMediaById, getRelated } from "@/lib/media";
import EpisodeList from "@/pages/detail/components/EpisodeList";

export default function DetailPage() {
  const { id = "" } = useParams();
  const item = getMediaById(id);
  const [showShareTip, setShowShareTip] = useState(false);

  usePageMeta(
    item ? `${item.title} - 影视详情 - 欧美日韩影视平台` : "影视详情 - 欧美日韩影视平台",
    item
      ? `${item.title}（${item.originalTitle}）：${item.synopsis.slice(0, 60)}... 导演${item.director}，${item.year}年${item.region}${item.type === "movie" ? "电影" : "电视剧"}。`
      : "影视作品详情页面",
    item ? `${item.title},${item.originalTitle},${item.genres.join(",")},影视详情` : "影视详情",
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!item) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-5">
            <i className="ri-emotion-sad-line text-4xl text-foreground-400" />
          </span>
          <h1 className="text-xl font-bold text-foreground-900 font-heading">
            未找到该影视作品
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            内容可能已下架或链接有误，返回首页继续浏览吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
          >
            <i className="ri-home-5-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelated(item, 5);
  const yearLabel =
    item.type === "tv" && item.endYear ? `${item.year} - ${item.endYear}` : `${item.year}`;

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__}detail/${item.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard may be unavailable in some environments */
    }
    setShowShareTip(true);
    window.setTimeout(() => setShowShareTip(false), 2200);
  };

  return (
    <SiteLayout>
      <div className="relative w-full h-[200px] md:h-[300px] overflow-hidden bg-primary-900">
        <img
          src={item.backdrop}
          alt={`${item.title} 剧照`}
          title={`${item.title} 影视详情`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/45 to-foreground-950/25" />
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-accent-500 text-background-50 mb-3">
            <i className="ri-movie-2-line" />
            {item.type === "movie" ? "电影" : "电视剧"}
          </span>
          <h1 className="text-2xl md:text-4xl font-extrabold text-background-50 font-heading leading-tight">
            {item.title}
          </h1>
          <p className="mt-1 text-sm md:text-base text-background-200 tracking-wide">
            {item.originalTitle}
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 pt-5">
        <PageBreadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: item.type === "movie" ? "电影" : "电视剧", to: item.type === "movie" ? "/category/movie" : "/category/tv" },
            { label: item.title },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-1">
            <div className="w-40 md:w-52 aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src={item.poster}
                alt={`${item.title} 海报`}
                title={`${item.title} 影视详情`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-4 bg-background-50 border border-background-200 rounded-lg p-4">
              <h3 className="text-sm font-bold text-foreground-950 font-heading mb-3">
                资料信息
              </h3>
              <dl className="space-y-2.5 text-sm">
                <InfoRow icon="ri-user-star-line" label="导演" value={item.director} />
                <InfoRow icon="ri-group-line" label="主演" value={item.cast.join(" / ")} />
                <InfoRow icon="ri-price-tag-3-line" label="类型" value={item.genres.join(" / ")} />
                <InfoRow icon="ri-earth-line" label="地区" value={`${item.region} · ${item.country}`} />
                <InfoRow icon="ri-calendar-line" label="年份" value={yearLabel} />
                {item.type === "movie" ? (
                  <InfoRow icon="ri-time-line" label="时长" value={item.duration ?? "-"} />
                ) : (
                  <>
                    <InfoRow icon="ri-play-list-line" label="集数" value={`${item.episodes ?? 0} 集`} />
                    <InfoRow icon="ri-loader-2-line" label="状态" value={item.status ?? "-"} />
                  </>
                )}
                <InfoRow icon="ri-fire-line" label="热度" value={formatHeat(item.heat)} />
              </dl>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex items-center flex-wrap gap-3">
              <RatingStars rating={item.rating} size="md" />
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-background-100 text-foreground-600">
                <i className="ri-fire-fill text-accent-500" />
                {formatHeat(item.heat)} 热度
              </span>
              {(item.tags ?? []).map((t) => (
                <span
                  key={t}
                  className="text-xs px-2 py-1 rounded-md bg-secondary-100 text-secondary-900"
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-5 bg-background-50 border border-background-200 rounded-lg p-5">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950 font-heading mb-3">
                <i className="ri-file-text-line text-primary-500" />
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                {item.synopsis}
              </p>
            </div>

            <div className="mt-5 rounded-lg overflow-hidden border border-background-200">
              <div className="relative w-full aspect-video bg-primary-950 flex flex-col items-center justify-center text-center px-4">
                <div className="absolute inset-0 opacity-40">
                  <img
                    src={item.backdrop}
                    alt={`${item.title} 预告`}
                    title={`${item.title} 影视详情`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="relative z-10 flex flex-col items-center">
                  <span className="w-14 h-14 rounded-full bg-background-50/20 border border-background-50/40 flex items-center justify-center mb-3 backdrop-blur-sm">
                    <i className="ri-play-fill text-2xl text-background-50" />
                  </span>
                  <p className="text-background-50 font-semibold text-sm md:text-base">
                    播放区域（演示占位）
                  </p>
                  <p className="text-background-200 text-xs md:text-sm mt-1">
                    本页面为内容展示演示，暂不提供在线播放功能
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-forward-line" />
                分享链接
              </button>
              <Link
                to={item.type === "movie" ? "/category/movie" : "/category/tv"}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-apps-2-line" />
                更多同类
              </Link>
            </div>

            {item.type === "tv" && (
              <div className="mt-5">
                <EpisodeList item={item} />
              </div>
            )}
          </div>
        </div>

        <section className="mt-12">
          <SectionHeading title="相关推荐" subtitle="根据类型与地区为你精选" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {related.map((r) => (
              <PosterCard key={r.id} item={r} />
            ))}
          </div>
        </section>
      </div>

      {showShareTip && (
        <div className="fixed bottom-24 md:bottom-8 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm shadow-none flex items-center gap-2">
          <i className="ri-checkbox-circle-fill text-accent-400" />
          链接已复制到剪贴板
        </div>
      )}
    </SiteLayout>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="flex items-center gap-1.5 shrink-0 w-14 text-foreground-500">
        <i className={`${icon} text-foreground-400`} />
        {label}
      </dt>
      <dd className="text-foreground-800 leading-snug">{value}</dd>
    </div>
  );
}